import { useState, useEffect, useCallback } from 'react';
import weekGeneratorService from '../services/weekGeneratorService';

const STORAGE_KEY = 'adhd_auto_generation';
const LAST_GENERATED_KEY = 'adhd_last_generated_week';

/**
 * Hook pour gérer la génération automatique hebdomadaire
 */
export function useAutoGeneration() {
  // Paramètres de génération automatique
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {
        enabled: false,
        mode: 'ask', // 'auto' | 'ask' | 'disabled'
        dayOfWeek: 1, // 1 = Lundi
        template: 'balanced',
        templateId: null, // ID d'un template personnalisé (prioritaire sur template)
        includeWeekends: true,
        autoAddHygiene: true,
        autoAddBreaks: true
      };
    } catch {
      return {
        enabled: false,
        mode: 'ask',
        dayOfWeek: 1,
        template: 'balanced',
        templateId: null,
        includeWeekends: true,
        autoAddHygiene: true,
        autoAddBreaks: true
      };
    }
  });

  // État pour le modal de confirmation
  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingGeneration, setPendingGeneration] = useState(null);

  // Sauvegarder les paramètres
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  /**
   * Mettre à jour les paramètres
   */
  const updateSettings = useCallback((updates) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Vérifier si on doit générer une nouvelle semaine
   */
  const checkShouldGenerate = useCallback(() => {
    if (!settings.enabled || settings.mode === 'disabled') return false;

    const now = new Date();
    const currentDay = now.getDay(); // 0 = Dimanche

    // Vérifier si c'est le jour configuré
    if (currentDay !== settings.dayOfWeek) return false;

    // Vérifier si on a déjà généré cette semaine
    const lastGenerated = localStorage.getItem(LAST_GENERATED_KEY);
    if (lastGenerated) {
      const lastDate = new Date(lastGenerated);
      const weekStart = getWeekStart(now);
      const lastWeekStart = getWeekStart(lastDate);

      // Si la dernière génération est dans la même semaine, ne pas regénérer
      if (weekStart.getTime() === lastWeekStart.getTime()) {
        return false;
      }
    }

    return true;
  }, [settings]);

  /**
   * Obtenir le début de la semaine (lundi)
   */
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  /**
   * Générer la semaine automatiquement
   */
  const generateWeek = useCallback((customTemplateApplier = null) => {
    const scheduleSettings = (() => {
      try {
        const saved = localStorage.getItem('adhd_schedule_settings');
        return saved ? JSON.parse(saved) : null;
      } catch {
        return null;
      }
    })();

    const today = new Date();

    // Si un template personnalisé est défini et qu'on a un applier
    if (settings.templateId && customTemplateApplier) {
      const weekTasks = customTemplateApplier(settings.templateId, today);
      if (weekTasks) {
        localStorage.setItem(LAST_GENERATED_KEY, today.toISOString());
        return weekTasks;
      }
    }

    // Sinon utiliser le générateur par défaut
    const weekTasks = weekGeneratorService.generateWeek(today, {
      template: settings.template,
      includeWeekends: settings.includeWeekends,
      autoAddHygiene: settings.autoAddHygiene,
      autoAddBreaks: settings.autoAddBreaks,
      schedule: scheduleSettings
    });

    localStorage.setItem(LAST_GENERATED_KEY, today.toISOString());
    return weekTasks;
  }, [settings]);

  /**
   * Déclencher la vérification et la génération
   */
  const triggerAutoGeneration = useCallback((customTemplateApplier = null) => {
    if (!checkShouldGenerate()) return null;

    if (settings.mode === 'auto') {
      // Générer automatiquement sans demander
      return generateWeek(customTemplateApplier);
    } else if (settings.mode === 'ask') {
      // Demander confirmation
      setShowPrompt(true);
      setPendingGeneration({ customTemplateApplier });
      return 'pending';
    }

    return null;
  }, [checkShouldGenerate, settings.mode, generateWeek]);

  /**
   * Confirmer la génération (après prompt)
   */
  const confirmGeneration = useCallback(() => {
    const result = generateWeek(pendingGeneration?.customTemplateApplier);
    setShowPrompt(false);
    setPendingGeneration(null);
    return result;
  }, [generateWeek, pendingGeneration]);

  /**
   * Annuler la génération
   */
  const cancelGeneration = useCallback(() => {
    // Marquer comme "traité" pour ne plus demander cette semaine
    localStorage.setItem(LAST_GENERATED_KEY, new Date().toISOString());
    setShowPrompt(false);
    setPendingGeneration(null);
  }, []);

  /**
   * Forcer la génération maintenant
   */
  const forceGenerate = useCallback((customTemplateApplier = null) => {
    return generateWeek(customTemplateApplier);
  }, [generateWeek]);

  return {
    settings,
    updateSettings,
    showPrompt,
    triggerAutoGeneration,
    confirmGeneration,
    cancelGeneration,
    forceGenerate,
    checkShouldGenerate
  };
}

export default useAutoGeneration;
